/**
 * The wire shapes F5-a's frontend consumes, transcribed READ-ONLY from the frozen Wave-1 contract
 * (`.forge/plans/F5-a/F5-a-Wave-1-contract.md § Field Definitions`). The server side is FS-6 on
 * `main` — a discovered divergence from this transcription is a coordinated FS-6/FS-3 revision, never
 * an edit here (contract § Read-Only to Implementers).
 *
 * `StubLoginRequest` is deliberately NOT carried here: `POST /api/v1/auth/stub-login` is F5-a WI-1.2's
 * call, and `apiClient.post` is generic over the body type, so F5-a WI-1.2 owns and transcribes it.
 */

/** The signed-in user, nested in `MeResponse.user`. */
export interface MeUser {
    /** The signed-in user's id (int64 on the wire). */
    readonly id: number;
    readonly displayName: string;
    readonly email: string;
    /** Who they report to; `null` at the top of the reporting line (`manager_id` self-reference). */
    readonly managerId: number | null;
}

/**
 * One element of `MeResponse.permissions[]` — the key and no other field
 * (`.forge/plans/F5-l/F5-l-Wave-1-contract.md § Permission`).
 *
 * A key the user does NOT hold is ABSENT from the array — never present-and-false (F5-a NFR-2 / AC-15).
 */
export interface MePermission {
    /** `<resource>.<action>` (e.g. `deal.view`). */
    readonly key: string;
}

/** The `200` body of `GET /api/v1/me`. */
export interface MeResponse {
    readonly user: MeUser;
    /**
     * The role's stable key (e.g. `sales_rep`). Display / effective-permissions input ONLY — the
     * client makes no authorization decision from it (AD #26). One role per user (AD #32).
     */
    readonly role: string;
    /**
     * The role's admin-editable display label (`role: "sales_rep"` → `"Sales Representative"`).
     * Additive to the key, for showing to the user; display only, never a decision input (AD #26).
     */
    readonly roleName: string;
    /** One row per held key, sorted; `manage ⟹ view` already expanded server-side; `*` never emitted. */
    readonly permissions: readonly MePermission[];
}

/**
 * The RFC 9457 problem envelope (`application/problem+json`) carried by every `4xx`/`5xx` body.
 * Extension members (`code`, `traceId`, `errors`) are flattened to top-level siblings of `status`.
 */
export interface ProblemBody {
    /** RFC default `about:blank`; clients never switch on it. */
    readonly type: string;
    readonly title: string;
    /** The HTTP status. */
    readonly status: number;
    /** Human-readable; never leaks a stack/SQL detail. Absent on some refusals. */
    readonly detail?: string;
    /** The request path. */
    readonly instance: string;
    /** The stable `UPPER_SNAKE` identifier clients switch on (`UNAUTHENTICATED`, `FORBIDDEN`, …). */
    readonly code: string;
    /** Present only when the MDC holds one (FS-5 wiring). */
    readonly traceId?: string;
    /** Present on a validation failure only. */
    readonly errors?: readonly unknown[];
    /**
     * Every other top-level member of the body — a route's own extension members, such as the
     * `existingTagId` and `existingTagActive` of `POST /api/v1/tags`'s `409` (F11-f Wave 1 contract
     * client-side fact 9; F11-f WI-1.3 plan Decision 3). Unnarrowed: the caller that expects a member
     * narrows it. Absent when the body carries none.
     */
    readonly extensions?: Readonly<Record<string, unknown>>;
}

/**
 * A non-2xx response from an authenticated API call, carrying the parsed problem envelope.
 *
 * F5-a keys off the HTTP `status`, not the body `code` (contract § Problem envelope) — `code` is
 * carried for fidelity and error messaging, not for the client's routing decision.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly code: string | undefined;
    readonly problem: ProblemBody | undefined;

    constructor(status: number, problem: ProblemBody | undefined, message?: string) {
        super(message ?? problem?.detail ?? problem?.title ?? `Request failed with status ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.code = problem?.code;
        this.problem = problem;
    }
}

/**
 * The `401` case, split out so a global handler and a caller can both `instanceof`-narrow it.
 * Thrown on the HTTP status alone — a `401` with any (or no) body `code` still lands here.
 */
export class UnauthorizedError extends ApiError {
    constructor(problem: ProblemBody | undefined) {
        super(401, problem, 'Not authenticated');
        this.name = 'UnauthorizedError';
    }
}
